import React from 'react';
import { aboutData } from '../../data/mock';
import { aboutTranslations } from '../../data/translations';
import { useLanguage } from '../../context/LanguageContext';
import { CheckCircle2, Code, Palette, Zap } from 'lucide-react';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';

const AboutSection = () => {
  const { t, language } = useLanguage();
  const translatedAbout = aboutTranslations[language] || aboutTranslations.de;

  return (
    <section id="about" className="py-24 lg:py-32 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-mid-purple/5 rounded-full blur-hero" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <AnimateOnScroll animation="fade-right">
            <div className="relative">
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-6 -left-6 w-20 h-20 bg-gradient-to-br from-mid-purple to-mid-pink rounded-2xl shadow-xl flex items-center justify-center z-10">
                  <Code size={28} className="text-white" />
                </div>
                
                <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-gradient-to-br from-mid-blue to-light-blue rounded-xl shadow-xl flex items-center justify-center z-10">
                  <Palette size={24} className="text-white" />
                </div>

                <div className="absolute top-1/2 -right-6 w-14 h-14 bg-gradient-to-br from-mid-orange to-mid-yellow rounded-lg shadow-xl flex items-center justify-center z-10">
                  <Zap size={20} className="text-white" />
                </div>
                
                {/* Main Card */}
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-grey/10 dark:border-gray-700">
                  <div className="aspect-square bg-gradient-to-br from-mid-purple/10 via-light-pink/15 to-mid-blue/10 dark:from-mid-purple/20 dark:via-light-pink/10 dark:to-mid-blue/20 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-mid-purple to-mid-pink flex items-center justify-center shadow-xl hover:scale-105 transition-transform duration-300 cursor-pointer">
                        <span className="text-5xl font-display font-bold text-white">BR</span>
                      </div>
                      <h3 className="font-display text-2xl font-bold text-black dark:text-white">{aboutData.name}</h3>
                      <p className="text-dark-grey dark:text-gray-400 mt-1">{t('about.jobTitle')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          {/* Content Side */}
          <AnimateOnScroll animation="fade-left" delay={0.2}>
            <div>
              <span className="inline-block bg-gradient-to-r from-mid-green/20 to-mid-blue/20 text-mid-green px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-green/20">
                {t('about.badge')}
              </span>
              
              <h2 className="font-display text-4xl md:text-5xl font-bold text-black dark:text-white mb-6">
                {t('about.title')}
                <br />
                <span className="gradient-text">{t('about.name')}</span>
              </h2>
              
              <p className="text-dark-grey dark:text-gray-400 leading-relaxed mb-8 text-lg">
                {translatedAbout.bio}
              </p>
              
              {/* Skills */}
              <div className="space-y-4">
                <h4 className="font-semibold text-black dark:text-white mb-4">{t('about.skills')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {translatedAbout.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-grey/20 dark:border-gray-700 hover:border-mid-green/30 transition-colors cursor-pointer group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-mid-green/10 flex items-center justify-center group-hover:bg-mid-green/20 transition-colors">
                        <CheckCircle2 size={16} className="text-mid-green" />
                      </div>
                      <span className="text-dark-grey dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
